import { Ban, Loader2, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import {
  MAX_ZMIAN_W_OPERACJI,
  podgladHurtu,
  zakolejkujHurt,
  type DraftChange,
  type PodgladHurtu as Podglad,
} from '@/lib/authoring-api';
import { useI18n } from '@/lib/i18n';
import { bezZablokowanych, numerZmiany } from '@/lib/podglad-hurtu';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PODGLĄD I ZAKOLEJKOWANIE ZMIANY HURTOWEJ — powyżej `MAX_ZMIAN_W_OPERACJI` zmian z siatki
 *  („Działania na bazie", etap 3, 29.09.2026).
 *
 *  ⚠️ OSOBNE OKNO, NIE `PodgladZmian` W PĘTLI: hurt to kilka operacji z JEDNYM potwierdzeniem
 *  podziału, a tysiąca kart werdyktu nikt nie przeczyta. Pokazujemy części, zablokowane i ostrzeżenia
 *  zgrupowane po kodzie; reguły i teksty potwierdzeń są te same co w zwykłym podglądzie (`cd.ack.*`).
 *
 *  ⚠️ „POMIŃ ZABLOKOWANE" JEST JAWNE: zmiany wypadają z wysyłki dopiero po kliknięciu, z liczbą,
 *  a podgląd liczy się od nowa. Serwer nigdy nie wycina ich sam.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function PodgladHurtu({
  groupId,
  changes,
  onClose,
  onQueued,
}: {
  groupId: string;
  changes: DraftChange[];
  onClose: () => void;
  /** Operacje, które weszły do kolejki, i powód, gdy któraś z kolejnych nie weszła. */
  onQueued: (opIds: string[], blad: string | null) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  /** Indeksy zmian z siatki, które wciąż idą — po „Pomiń zablokowane" lista się kurczy. */
  const [aktywne, setAktywne] = useState<number[]>(() => changes.map((_, i) => i));
  const wysylane = useMemo(() => aktywne.map((i) => changes[i]!), [aktywne, changes]);
  const [podglad, setPodglad] = useState<Podglad | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setPodglad(null);
    setError(null);
    if (wysylane.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    podgladHurtu(groupId, wysylane)
      .then((p) => alive && setPodglad(p))
      .catch((err) => alive && setError(err instanceof ApiError ? err.message : t('common.error')))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [groupId, wysylane, t]);

  /** Numer zmiany tak, jak widział ją człowiek na liście „do wysłania" — nie pozycja po pominięciach. */
  const nr = (index: number): number => numerZmiany(aktywne, index);
  const pominietych = changes.length - aktywne.length;
  const allAcked = (podglad?.requiresAck ?? []).every((c) => acked.has(c));
  const canQueue = podglad !== null && podglad.blocked === 0 && allAcked && note.trim().length >= 10 && !saving;

  const pomin = (): void => {
    if (podglad === null) return;
    setAktywne((a) => bezZablokowanych(a, podglad.zablokowane));
  };

  const zakolejkuj = async (): Promise<void> => {
    if (podglad === null) return;
    setSaving(true);
    setError(null);
    try {
      const r = await zakolejkujHurt({
        groupId,
        changes: wysylane,
        authorNote: note.trim(),
        previewToken: podglad.token,
        acknowledged: [...acked],
      });
      if (r.blad === null) toast.success(t('cd.queued'));
      onQueued(r.opIds, r.blad);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={t('baza.hurtTytul')}
        description={
          podglad === null
            ? t('baza.doWyslania').replace('{n}', String(wysylane.length))
            : t('baza.hurtOpis')
                .replace('{n}', String(podglad.zmian))
                .replace('{k}', String(podglad.czesci.length))
                .replace('{max}', String(MAX_ZMIAN_W_OPERACJI))
        }
        className="w-[min(64rem,calc(100vw-2rem))]"
      >
        {loading ? (
          <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('cd.previewRunning')}
          </p>
        ) : (
          <div className="space-y-3">
            {podglad !== null && (
              <div className="flex flex-wrap gap-1">
                {podglad.czesci.map((c) => (
                  <span key={c.nr} className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px]">
                    {c.nr} · {c.akcja} · {c.zmian}
                  </span>
                ))}
              </div>
            )}
            {pominietych > 0 && (
              <p className="text-xs text-muted-foreground">{t('baza.hurtPominiete').replace('{n}', String(pominietych))}</p>
            )}

            {podglad !== null && podglad.blocked > 0 && (
              <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="destructive">
                    <Ban className="size-3" />
                    {t('baza.hurtZablokowane').replace('{n}', String(podglad.blocked))}
                  </Badge>
                  <Button size="sm" variant="outline" className="ml-auto" onClick={pomin}>
                    {t('baza.hurtPomin').replace('{n}', String(podglad.blocked))}
                  </Button>
                </div>
                <ul className="max-h-48 space-y-1 overflow-y-auto text-xs">
                  {podglad.zablokowane.map((z) => (
                    <li key={z.index}>
                      <span className="font-mono">
                        #{nr(z.index)}{' '}
                        {Object.entries(z.pk)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(', ')}
                      </span>
                      <span className="text-destructive"> — {z.errors.join('; ')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {podglad !== null && podglad.ostrzezenia.length > 0 && (
              <ul className="space-y-1.5 text-xs text-[var(--warning)]">
                {podglad.ostrzezenia.map((o) => (
                  <li key={o.code} className="flex items-start gap-1.5">
                    <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                    <span>
                      {o.message}
                      {/* Liczba i pierwsza zmiana tylko przy części zmian — przy wszystkich nic nie wnoszą. */}
                      {o.zmian < podglad.zmian && (
                        <span className="text-muted-foreground">
                          {' '}
                          ({t('baza.hurtIleZmian').replace('{n}', String(o.zmian)).replace('{nr}', String(nr(o.pierwsza)))})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {podglad !== null && <p className="text-xs text-muted-foreground">{t('cd.previewIsPortalOnly')}</p>}

            {(podglad?.requiresAck.length ?? 0) > 0 && (
              <div className="space-y-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3">
                <p className="text-xs font-medium">{t('cd.ackTitle')}</p>
                {podglad?.requiresAck.map((code) => (
                  <label key={code} className="flex items-start gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={acked.has(code)}
                      onChange={(e) =>
                        setAcked((s) => {
                          const next = new Set(s);
                          if (e.target.checked) next.add(code);
                          else next.delete(code);
                          return next;
                        })
                      }
                    />
                    <span>{t(`cd.ack.${code}`)}</span>
                  </label>
                ))}
              </div>
            )}

            <FormGrid>
              <Field label={t('cd.authorNote')} htmlFor="note-hurt" required hint={t('cd.authorNoteHint')}>
                <Input
                  id="note-hurt"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('cd.authorNotePlaceholder')}
                  autoComplete="off"
                />
              </Field>
              <ModalError>{error}</ModalError>
            </FormGrid>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!canQueue} onClick={() => void zakolejkuj()}>
            {saving && <Loader2 className="animate-spin" />}
            {t('cd.queueAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
